import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { of } from "rxjs";
import { catchError, filter, map, switchMap, withLatestFrom } from "rxjs/operators";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { currentUserData } from "../selectors/authentication.selectors";
import {
  ScenariosCommandsStoreAction,
  StorageNativeStoreAction,
} from "src/components/shared-components/enum/enumStoreAction";
import {
  scenariosCommandsLoadErrorAction,
  scenariosCommandsLoadFromApiAction,
  scenariosCommandsLoadSuccessAction,
  scenariosCommandsUnsetKeyStorageAction,
} from "../actions/scenario-commands.action";
import { scenariosCommandsFeature } from "../selectors/scenario-commands.selector";
import { ScenariosCommandsState } from "../reducers/scenario-commands.reducer";
import { ScenariosService } from "src/service/scenarios.service";
import { ScenarioCommand } from "src/components/shared-components/interfaces/iScenarios";

/**
 * Handle side effects for scenario commands store slice
 */
@Injectable()
export class ScenarioCommandsEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _scenariosService = inject(ScenariosService);
  private readonly _store = inject(Store);


  /**
   * Effect who catch main event and unset specific server key storage
   */
  public scenariosCommandsUnsetKeyEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(StorageNativeStoreAction.STORAGE_UNSET_ALL_AUTH_KEY),
      map(() => scenariosCommandsUnsetKeyStorageAction())
    )
  );

  /**
   * Initialize the loading of scenario commands for a given scenario
   */
  public scenarioCommandsStartInitEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ScenariosCommandsStoreAction.SCENARIOS_COMMANDS_LOAD_INIT_START),
      withLatestFrom(this._store.select(currentUserData)),
      filter(
        (actionWithUser) =>
          !!(actionWithUser[1] as GenericUserData)?.currentUser
      ),
      map((actionWithUser: any) => {
        const action: { idScenario: number } = actionWithUser[0];
        return scenariosCommandsLoadFromApiAction({
          idScenario: action.idScenario,
        });
      })
    )
  );

  /**
   * Load commands list for a given scenario from API
   */
  public scenarioCommandsLoadApiEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ScenariosCommandsStoreAction.SCENARIOS_COMMANDS_LOAD_FROM_API),
      switchMap((action: any) => {
        return this._scenariosService.getScenarioCommandList(action.idScenario).pipe(
          map((commands: ScenarioCommand[]) =>
            scenariosCommandsLoadSuccessAction({
              idScenario: action.idScenario,
              commands: commands ?? [],
            })
          ),
          catchError((error: any) =>
            of(
              scenariosCommandsLoadErrorAction({
                idScenario: action.idScenario,
                errorCode: error?.status ?? 0,
              })
            )
          )
        );
      })
    )
  );

  /**
   * When a command change is notified by the broker, reload the commands list for
   * that scenario from API, but only if those commands are already in the store
   * (i.e. the user has opened that scenario): there is no single-command GET, so the
   * whole list is refreshed
   */
  public scenarioCommandsChangeReceivedEffect = createEffect(() =>
    this._actions$.pipe(
      ofType(ScenariosCommandsStoreAction.SCENARIOS_COMMANDS_CHANGE_RECEIVED),
      withLatestFrom(this._store.select(scenariosCommandsFeature)),
      filter(
        ([action, state]: [{ idScenario: number }, ScenariosCommandsState]) =>
          !!state?.[action.idScenario]
      ),
      map(([action]: [{ idScenario: number }, ScenariosCommandsState]) =>
        scenariosCommandsLoadFromApiAction({ idScenario: action.idScenario })
      )
    )
  );
}
